import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { MATCH_UNMATCH, MISMATCH_REASONS_REVIEW } from '../../../../utils/constants/insights-filter';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import {
    tryToFetchUnreconciledTransactions,
    tryToReviewTransaction
} from '../../../../store/brokers/partners/transactions/transactionsSlice';
import ReviewTable from '../tables/ReviewTable';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import Input from '../../../../shared/input';

interface IReviewTransactionsModal {
    openModal: boolean;
    transactions?: any;
    mainTransaction?: any;
    columns?: any;
    status: string;
    handleCloseModal: () => void;
    onReviewSuccessfully: (reviewId?: string, status?: string) => void;
}

const ReviewTransactionsModal = ({
    openModal,
    columns,
    mainTransaction,
    status,
    transactions,
    handleCloseModal,
    onReviewSuccessfully,
}: IReviewTransactionsModal) => {
    const dispatch = useAppDispatch();
    const [selectedIds, setSelectedIds] = useState<any>();
    const [selectedReason, setSelectedReason] = useState<any>();
    const [selectedPotentialMatch, setSelectedPotentialMatch] = useState<any>();
    const [selectReasonForAll, setSelectReasonForAll] = useState<any>()
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string>();
    const [selectedUnreconciledIds, setSelectedUnreconciledIds] = useState<any>();
    const [selectedUnreconciledRows, setSelectedUnreconciledRows] = useState<any>();
    const [search, setSearch] = useState<any>();
    const [searchAmount, setSearchAmount] = useState<any>();
    const [tabActive, setTabActive] = useState<string>('pos')
    const [toleranceAmount, setToleranceAmount] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [sortSelected, setSortSelected] = useState<any>();
    const [rows, setRows] = useState<any>();
    const unreconciledData = useAppSelector((state) => state?.transactions)
    const paginationData = useAppSelector(state => state?.transactions?.unreconciled?.data?.page)

    useEffect(() => {
        if (unreconciledData?.unreconciled) {
            setRows(unreconciledData?.unreconciled?.data?.elements || [])
        }
    }, [unreconciledData?.unreconciled])

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const onGettingUnreconciledData = async () => {
        try {
            let dataFormat: any = {
                ...paginationState || {},
                data: {
                    transactionId: mainTransaction?.transactionId,
                    transactionType: tabActive
                }
            }
            if (search && search !== '') {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        search: search
                    }
                }
            }
            if (searchAmount && searchAmount !== '') {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        searchValue: searchAmount
                    }
                }
            }
            if (toleranceAmount && toleranceAmount !== '') {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        tolerance: toleranceAmount
                    }
                }
            }
            if (sortSelected && (sortSelected?.psp)) {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        pspDateSort: sortSelected?.psp === 'new' ? -1 : 1
                    }
                }
            }
            if (sortSelected && (sortSelected?.pos)) {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        posDateSort: sortSelected?.pos === 'new' ? -1 : 1
                    }
                }
            }
            if (sortSelected && (sortSelected?.pspValue)) {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        pspValue: sortSelected?.pspValue === 'big' ? -1 : 1
                    }
                }
            }
            if (sortSelected && (sortSelected?.posValue)) {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        posValue: sortSelected?.posValue === 'big' ? -1 : 1
                    }
                }
            }
            if (sortSelected && (sortSelected?.variance)) {
                dataFormat = {
                    ...dataFormat || {},
                    data: {
                        ...dataFormat?.data || {},
                        variance: sortSelected?.variance === 'big' ? -1 : 1
                    }
                }
            }
            await dispatch(tryToFetchUnreconciledTransactions(dataFormat)).unwrap();
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        if (status === 'unreconciled') {
            onGettingUnreconciledData()
        }
    }, [paginationState])

    useEffect(() => {
        if (status === 'unreconciled') {
            onGettingUnreconciledData()
        }
    }, [status])

    const onSelectRow = (row: string) => {
        const findIfAlreadySelected = selectedIds && selectedIds?.length > 0 && selectedIds?.find((item: any) => item === row)
        if (findIfAlreadySelected) {
            const filterOthers = selectedIds && selectedIds?.length > 0 && selectedIds?.filter((item: any) => item !== row)
            setSelectedIds(filterOthers)
        } else {
            setSelectedIds((prev: any) => [...prev || [], row])
        }
    }

    const onSelectAll = () => {
        if (selectedIds?.length === ((transactions?.length || 0) + 1)) {
            setSelectedIds(undefined)
        } else {
            const formatIds = transactions?.map((item: any) => item?.transactionId)
            setSelectedIds([mainTransaction?.transactionId, ...formatIds || []])
        }
    }

    const onSelectUnreconciledRow = (row: any) => {
        const findIfAlreadySelected = selectedUnreconciledIds && selectedUnreconciledIds?.length > 0 && selectedUnreconciledIds?.find((item: any) => item === row?.transactionId)
        if (findIfAlreadySelected) {
            const filterOthers = selectedUnreconciledIds && selectedUnreconciledIds?.length > 0 && selectedUnreconciledIds?.filter((item: any) => item !== row?.transactionId)
            setSelectedUnreconciledIds(filterOthers)
            const filterSelectedRows = selectedUnreconciledRows && selectedUnreconciledRows?.length > 0 && selectedUnreconciledRows?.filter((item: any) => item?.transactionId !== row?.transactionId)
            setSelectedUnreconciledRows(filterSelectedRows)
        } else {
            setSelectedUnreconciledIds((prev: any) => [...prev || [], row?.transactionId])
            setSelectedUnreconciledRows((prev: any) => [...prev || [], row])
        }
    }

    const onSelectUnreconciledAll = () => {
        setSelectedIds(rows?.map((item: any) => item?.transactionId))
    }

    const onCloseModal = () => {
        handleCloseModal();
        setSubmitError(undefined)
        setSelectedIds(undefined)
        setSelectedPotentialMatch(undefined)
        setSubmitError(undefined)
        setLoadingSubmit(false)
        setSelectReasonForAll(undefined)
        setSelectedUnreconciledIds(undefined)
        setSelectedUnreconciledRows(undefined)
        setSearch(undefined)
        setSearchAmount(undefined)
        setSelectedReason(undefined)
    };

    const onChangeSelectedValue = (e: any, status?: string, id?: any) => {
        if (id) {
            if (status === 'potential match') {
                setSelectedPotentialMatch((prev: any) => ({
                    ...prev || {},
                    [id]: e?.value
                }))
            }
            if (status === 'reconciled' || status === 'voided transaction') {
                setSelectedReason((prev: any) => ({
                    ...prev || {},
                    [id]: e?.value
                }))
            }
        }
    }

    const onChangeMisMatchReasonForAll = (value: any, type?: string) => {
        if (!selectedIds || selectedIds?.length === 0) {
            setSelectedIds(transactions?.map((item: any) => item?.transactionId))
            setSelectReasonForAll(value)
            let formatReasonForAll: any = {}
            transactions?.map((item: any) => {
                formatReasonForAll = {
                    ...formatReasonForAll || {},
                    [item?.transactionId || '']: value?.value
                }
                return item
            })
            setSelectedReason(formatReasonForAll)
        } else if (selectedIds) {
            let formatReasonForAll: any = {}
            selectedIds?.map((item: string) => {
                formatReasonForAll = {
                    ...formatReasonForAll || {},
                    [item || '']: value?.value
                }
                return item
            })
            setSelectedReason(formatReasonForAll)
            setSelectReasonForAll(value)
        }
    }

    const handleSortChange = (sortOrder: string, type: string) => {
        const newType = sortSelected?.[type] === sortOrder ? undefined : sortOrder
        setSortSelected((prev: any) => ({
            ...prev || {},
            [type]: newType
        }))
        onResetPaginationState();
    };

    const onChangeMatchUnmatchForAll = (value: any, type?: string) => {
        setSelectReasonForAll(value)
        let formatReasonForAll: any = {}
        transactions && transactions?.map((item: any) => {
            formatReasonForAll = {
                ...formatReasonForAll || {},
                [item?.transactionId || '']: value?.value
            }
            return item
        })
        setSelectedReason(formatReasonForAll)
    }

    const onConfirm = async () => {
        let newStatus: string
        try {
            setLoadingSubmit(true)
            setSubmitError(undefined)
            let formatData: any = {}
            if (status === 'unreconciled') {
                const mainTransactions = { transactionId: mainTransaction?.transactionId, matched: true }
                const transactionsFormat = selectedUnreconciledIds && selectedUnreconciledIds?.length > 0 && selectedUnreconciledIds?.map((item: string) => {
                    return {
                        transactionId: item,
                        matched: true
                    }
                })
                formatData = {
                    mainTransaction: mainTransactions,
                    transactions: transactionsFormat
                }
                newStatus = 'reconciled'
                await dispatch(tryToReviewTransaction(formatData)).unwrap()
                onReviewSuccessfully(mainTransaction?.transactionId, newStatus);
                onCloseModal()
            }
            else if (status === 'reconciled' || status === 'voided transaction') {
                const mainTransactions = selectedReason?.[mainTransaction?.transactionId] ? { transactionId: mainTransaction?.transactionId, matched: false, reason: selectedReason?.[mainTransaction?.transactionId] } : { transactionId: mainTransaction?.transactionId }
                const keysObjects = selectedReason && Object?.keys(selectedReason)?.filter((item: string) => item !== mainTransaction?.transactionId)
                const transactionsFormat = keysObjects && keysObjects?.length > 0 && keysObjects?.map((item: any) => {
                    return {
                        transactionId: item,
                        reason: selectedReason?.[item],
                        matched: false
                    }
                })
                formatData = {
                    mainTransaction: mainTransactions,
                    transactions: transactionsFormat
                }
                newStatus = 'unreconciled'
                await dispatch(tryToReviewTransaction(formatData)).unwrap()
                onReviewSuccessfully(mainTransaction?.transactionId, !mainTransaction?.matched ? newStatus : undefined);
                onCloseModal()
            } else if (status === 'potential match') {
                const mainTransactions = selectedReason?.[mainTransaction?.transactionId] ? { transactionId: mainTransaction?.transactionId, matched: selectedReason?.[mainTransaction?.transactionId] === 'match' } : { transactionId: mainTransaction?.transactionId }
                const keysObjects = selectedReason && Object?.keys(selectedReason)?.filter((item: string) => item !== mainTransaction?.transactionId)
                const transactionsFormat = keysObjects && keysObjects?.length > 0 && keysObjects?.map((item: any) => {
                    return {
                        transactionId: item,
                        matched: selectedReason?.[item] === 'match'
                    }
                })
                formatData = {
                    mainTransaction: mainTransactions,
                    transactions: transactionsFormat
                }
                await dispatch(tryToReviewTransaction(formatData)).unwrap()
                onReviewSuccessfully(mainTransaction?.transactionId);
                onCloseModal()
            }
        } catch (err) {
            setSubmitError(`${err}`)
        }
        setLoadingSubmit(false)
    }

    const onChangeActiveTab = (tab: string) => {
        if (tab !== tabActive) {
            setTabActive(tab)
            onResetPaginationState()
        }
    }

    const onChangeSearch = (value: any, type?: string) => {
        if (type === 'search') {
            setSearch(value)
        } if (type === 'toleranceAmount') {
            setToleranceAmount(value)
        } else {
            setSearchAmount(value)
        }
        onResetPaginationState()
    }

    const confirmEnabled = status === 'unreconciled' ? selectedUnreconciledIds?.length > 0
        : !!selectedReason
    return (
        <Modal
            open={openModal}
            title='Review Reconciliation'
            headerStyle='border-b-1 py-3 mb-0 min-w-full min-h-full'
            onClose={onCloseModal}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[85%] lg:min-w-[85%] min-h-[100vh]'>
            <div className='p-2 min-w-[95%] md:min-w-[80%] lg:min-w-[80%] min-h-[400px]'>
                {status === 'reconciled' &&
                    <div className='flex flex-col md:flex-row md:items-center my-5 w-full'>
                        <div className='mb-3 md:mb-0 mr-4'>
                            <p className='dark:text-primary-dark'>Mismatch Reason for all transactions in this entry</p>
                            <p className='text-primary-light'>You can also select a reason specifically to a transaction</p>
                        </div>
                        <SelectCheckbox
                            placeholder='Select Reason'
                            containerStyle='w-auto min-w-[250px]'
                            options={MISMATCH_REASONS_REVIEW?.filter((item: any) => item?.value !== 'all')}
                            selectedOption={selectReasonForAll}
                            onChangeSelectedOption={onChangeMisMatchReasonForAll}
                        />
                    </div>
                }
                {status === 'voided transaction' &&
                    <div className='my-5'>
                        <p className='dark:text-primary-dark'>Voided Transactions</p>
                        <p className='text-primary-light'>Add reason to unreconcile each, if only one reason is added to one of transaction, the other will be flagged with the same reason until specifically changed.</p>
                    </div>
                }
                {status === 'potential match' &&
                    <div className='flex flex-col md:flex-row md:items-center my-3'>
                        <div className='mb-3 md:mb-0'>
                            <p className='dark:text-primary-dark'>Match or unmatch transactions for all</p>
                            <p className='text-primary-light'>You can also do this individually on the table below</p>
                        </div>
                        <SelectCheckbox
                            placeholder='Choose One'
                            options={MATCH_UNMATCH}
                            containerStyle='w-auto'
                            selectedOption={selectReasonForAll}
                            onChangeSelectedOption={onChangeMatchUnmatchForAll}
                        />
                    </div>
                }
                {submitError && <Error text={submitError} />}
                {status === 'unreconciled' &&
                    <>
                        <div className='border border-1 p-2'>
                            <ReviewTable
                                status={''}
                                mainTransaction={true}
                                columns={columns || []}
                                rows={[mainTransaction || {}, ...selectedUnreconciledRows || []]}
                                onChangeSelectedValue={onChangeSelectedValue}
                            />
                        </div>
                        <div className='border border-1 p-2'>
                            <div className='my-4 w-fit border border-1  border-[#F5F5F5] rounded-full bg-white dark:bg-black flex flex-row items-center justify-between py-1 px-1'>
                                <div
                                    onClick={() => onChangeActiveTab('pos')}
                                    className={`text-md px-3 cursor-pointer text-center hover:opacity-90 ${tabActive === 'pos' ? 'bg-[#252B37] dark:bg-bgColor-darkMode text-white dark:text-white rounded-full' : 'text-black dark:text-gray-600'} mr-2`}>
                                    <p>Match POS Transaction</p>
                                </div>
                                <div
                                    onClick={() => onChangeActiveTab('psp')}
                                    className={`text-md px-3 cursor-pointer text-center mb-2 md:mb-0 hover:opacity-90 ${tabActive === 'psp' ? 'bg-[#252B37] dark:bg-bgColor-darkMode text-white dark:text-white rounded-full' : 'text-black dark:text-gray-600'}`}>
                                    <p>Match PSP Transaction</p>
                                </div>
                            </div>

                            <div className='my-5 flex flex-row items-center'>
                                <Input
                                    label='Search by Reference'
                                    showValue={true}
                                    inputValue={search}
                                    inputUniqueName='search'
                                    containerStyle='mr-8'
                                    onChangeInput={onChangeSearch}
                                />
                                <Input
                                    label='Search by Amount'
                                    showValue={true}
                                    inputValue={searchAmount}
                                    containerStyle='mr-4'
                                    inputUniqueName='searchAmount'
                                    onChangeInput={onChangeSearch}
                                />
                                <Input
                                    label='Amount Tolerance'
                                    showValue={true}
                                    inputValue={toleranceAmount}
                                    containerStyle='mr-4'
                                    inputUniqueName='toleranceAmount'
                                    onChangeInput={onChangeSearch}
                                />
                            </div>
                            <ReviewTable
                                status={status || ''}
                                columns={columns || []}
                                rows={rows || []}
                                unreconciled={true}
                                sortSelected={sortSelected}
                                paginationData={paginationData}
                                paginationState={paginationState}
                                handleSortChange={handleSortChange}
                                setPaginationState={setPaginationState}
                                selectedIds={selectedUnreconciledIds}
                                onSelectRow={onSelectUnreconciledRow}
                                onChangeSelectedValue={onChangeSelectedValue}
                                onSelectAll={onSelectUnreconciledAll}
                            />
                        </div>
                    </>
                }
                {status !== 'unreconciled' &&
                    <ReviewTable
                        status={status || ''}
                        columns={columns || []}
                        rows={(transactions && transactions?.length > 0 && mainTransaction) ? [mainTransaction, ...transactions || []] : [mainTransaction]}
                        selectedIds={selectedIds}
                        selectedReason={selectedReason}
                        selectedPotentialMatch={selectedPotentialMatch}
                        onChangeSelectedValue={onChangeSelectedValue}
                        onSelectRow={onSelectRow}
                        onSelectAll={onSelectAll}
                    />
                }
                <div className='flex flex-cols md:flex-row md:justify-between items-center mt-10 mb-4'>
                    <div>

                    </div>
                    <Button
                        className={`${(confirmEnabled && !loadingSubmit) ? 'btn-main dark:bg-black dark:text-primary-dark' : 'btn-main-disable dark:bg-black opacity-80'} !shadow-none min-w-[130px] !rounded `}
                        label={loadingSubmit ? 'Confirming...' : 'Confirm'}
                        dataQa={'submit-btn'}
                        onClickButton={() => (confirmEnabled && !loadingSubmit) && onConfirm()}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ReviewTransactionsModal;